<template>
  <hr-form-dialog
    ref="dialog"
    size="lg"
    :title="$t('sessionActivityReports.restoreDeletedActivities')"
    @hide="onDialogHide"
  >
    <div class="row q-col-gutter-md">
      <p class="col-12 q-ma-none">
        {{ $t('sessionActivityReports.selectActivitiesToRestore') }}
      </p>
      <div
        v-if="isSelectionLimitExceeded"
        class="col-12"
      >
        <hr-info-banner
          :message="$t('sessionActivityReports.restoreLimitMessage', { number: maxSelectableActivities })"
        />
      </div>
      <div class="col-12">
        <session-activities-table
          ref="sessionActivitiesTable"
          v-model:selected="selectedSessionActivities"
          :default-columns="[
            'status', 'client', 'startTimestamp','endTimestamp', 'duration', 'userName','type', 'sessionName', 'id'
          ]"
          disable-export
          disable-export-with-data
          disable-views
          disable-send-to-integration
          disable-cursor-pointer
          hide-pagination-label
          is-deleted-list
          bordered
        />
      </div>
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="onCancel"
      />
      <universal-tooltip
        :show-tooltip="isSelectionLimitExceeded"
        :text="$t('sessionActivityReports.restoreLimitMessage', { number: maxSelectableActivities })"
      >
        <hr-confirm-btn
          :label="$t('sessionActivityReports.restoreButton')"
          :disable="!hasSelectedSessionActivities || isSelectionLimitExceeded"
          @click="restoreActivities"
        />
      </universal-tooltip>
    </template>
  </hr-form-dialog>
</template>

<script>
import { callFirebaseFunction } from 'boot/firebase';
import FailedSessionActivitiesList from 'components/SessionActivities/Modals/FailedSessionActivitiesList';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import { notifyNegative, notifySuccess } from 'src/composables/interactions';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrInfoBanner,
} from 'src/design-system';
import Interactions from 'src/mixins/Interactions';
import { defineAsyncComponent } from 'vue';

export default {
  name: 'RecoverSessionActivitiesDialog',

  components: {
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrInfoBanner,
    UniversalTooltip,
    SessionActivitiesTable: defineAsyncComponent(() => import('components/Sessions/SessionActivitiesTable.vue')),
  },

  mixins: [Interactions],

  emits: ['hide', 'ok'],

  data() {
    return {
      selectedSessionActivities: [],
      maxSelectableActivities: 100,
    };
  },

  computed: {
    hasSelectedSessionActivities() {
      return this.selectedSessionActivities.length > 0;
    }, // hasSelectedActivities()

    isSelectionLimitExceeded() {
      return this.selectedSessionActivities.length > this.maxSelectableActivities;
    }, // isSelectionLimitExceeded()
  },

  methods: {
    show() {
      this.$refs.dialog.show();
    }, // show()

    onCancel() {
      this.hide();
    }, // onCancel()

    hide() {
      this.$emit('hide');

      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    }, // hide()

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    }, // onDialogHide()

    async restoreActivities() {
      if (!this.selectedSessionActivities.length) {
        return;
      }

      try {
        this.$q.loading.show();
        const selectedSessionActivitiesIds = this.selectedSessionActivities.map(({ id }) => id);

        const response = await callFirebaseFunction('recoverSessionActivities', {
          sessionActivitiesIdsToRecover: selectedSessionActivitiesIds,
        });

        if (response?.data?.status === 'partial_failure') {
          this.openComponentDialog(FailedSessionActivitiesList, {
            failedActivityIds: response.data.failedActivitiesIds,
          });
          return;
        }

        const message = this.$t(
          `sessionActivityReports.${selectedSessionActivitiesIds.length > 1
            ? 'pluralRestoredActivitiesMessage' : 'singularRestoredActivitiesMessage'}`,
          { number: selectedSessionActivitiesIds.length },
        );

        notifySuccess(message);
      } catch (err) {
        notifyNegative(err.message);
      } finally {
        if (this?.$refs?.sessionActivitiesTable) {
          this.$refs.sessionActivitiesTable.refreshAll();
        }
        this.$emit('ok');
        this.$q.loading.hide();
        this.hide();
      }
    },
  },
};
</script>
